import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { COMPETITION_API_URL } from '../config/api-url';

const withErrorHandling = async (fn) => {
  try {
    const response = await fn();
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || error.response?.data?.detail || error.message;
    throw new Error(message || 'Competition service request failed');
  }
};

const competitionClient = axios.create({
  baseURL: COMPETITION_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const get = (path, params = {}) =>
  withErrorHandling(() => competitionClient.get(path, { params }));

const post = (path, payload = {}) =>
  withErrorHandling(() => competitionClient.post(path, payload));

export const fetchCompetitionByMarket = createAsyncThunk(
  'competition/fetchByMarket',
  async ({ alias, marketId, dateFrom, dateTo }) => {
    const trimmedAlias = alias?.trim();
    const trimmedMarketId = marketId?.trim();

    if (!trimmedAlias && !trimmedMarketId) {
      throw new Error('Alias or market ID is required');
    }

    if (!dateFrom || !dateTo) {
      throw new Error('Both dateFrom and dateTo must be provided');
    }

    const params = {
      date_from: dateFrom,
      date_to: dateTo,
    };

    if (trimmedAlias) {
      params.alias = trimmedAlias;
    }
    if (trimmedMarketId) {
      params.market_id = trimmedMarketId;
    }

    const data = await get('/competition/by_market', params);
    return { alias: trimmedAlias, marketId: trimmedMarketId, data, dateFrom, dateTo };
  }
);

export const fetchCompetitionByEvent = createAsyncThunk(
  'competition/fetchByEvent',
  async ({ eventId }) => {
    if (!eventId || !eventId.trim()) {
      throw new Error('Event ID is required');
    }

    const safeId = encodeURIComponent(eventId.trim());
    const data = await get(`/competition/by_event/${safeId}`);
    return { eventId: eventId.trim(), data };
  }
);

export const resolveCompetitionAlias = createAsyncThunk(
  'competition/resolveAlias',
  async ({ alias, context = {} }) => {
    if (!alias || !alias.trim()) {
      throw new Error('Alias text is required to run the resolver');
    }

    const payload = {
      alias: alias.trim(),
      ...context,
    };

    const data = await post('/competition/normalize/resolve', payload);
    return { alias: alias.trim(), data };
  }
);

export const persistCompetitionAlias = createAsyncThunk(
  'competition/persistAlias',
  async ({ alias, marketId, metadata = {} }) => {
    if (!alias || !alias.trim()) {
      throw new Error('Alias is required to persist');
    }

    if (!marketId) {
      throw new Error('A canonical market must be selected');
    }

    const payload = {
      alias: alias.trim(),
      market_id: marketId,
      ...metadata,
    };

    const data = await post('/competition/normalize/alias', payload);
    return { alias: alias.trim(), data };
  }
);

export const resetCompetitionAlias = createAsyncThunk(
  'competition/resetAlias',
  async ({ alias }) => {
    if (!alias || !alias.trim()) {
      throw new Error('Alias is required to reset');
    }

    const payload = { alias: alias.trim() };
    const data = await post('/competition/normalize/alias/reset', payload);
    return { alias: alias.trim(), data };
  }
);

export const fetchLinkedCompetition = createAsyncThunk(
  'competition/fetchLinkedCompetition',
  async ({ dateFrom, dateTo, windowDays = 14 }) => {
    if (!dateFrom || !dateTo) {
      throw new Error('dateFrom and dateTo are required');
    }

    const params = {
      date_from: dateFrom,
      date_to: dateTo,
      window_days: windowDays,
    };

    const data = await get('/competition/linked', params);
    return { params: { dateFrom, dateTo, windowDays }, data };
  }
);
